import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

declare global {
  interface Window {
    plausible: (event: string, options?: { props?: Record<string, string | number | boolean> }) => void;
  }
}

const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

// Track page views with error handling
export const trackPageView = (url: string) => {
  try {
    if (window.plausible) {
      window.plausible('pageview', { 
        props: { 
          url,
          secure: window.location.protocol === 'https:',
          host: window.location.host,
          referrer: document.referrer,
          language: navigator.language,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          environment: isDevelopment ? 'development' : 'production'
        } 
      });
    }
  } catch (error) {
    if (!isDevelopment) {
      console.warn('Analytics tracking failed:', error);
    }
  }
};

// Track custom events with error handling
export const trackEvent = (eventName: string, props?: Record<string, string | number | boolean>) => {
  try {
    if (window.plausible) {
      window.plausible(eventName, { 
        props: {
          ...props,
          secure: window.location.protocol === 'https:',
          host: window.location.host,
          path: window.location.pathname,
          timestamp: new Date().toISOString(),
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          language: navigator.language,
          environment: isDevelopment ? 'development' : 'production'
        }
      });
    }
  } catch (error) {
    if (!isDevelopment) {
      console.warn('Analytics event tracking failed:', error);
    }
  }
};

// Track outbound links
export const trackOutboundLink = (url: string, text?: string) => {
  try {
    trackEvent('outbound_link_click', { 
      url,
      text: text || (event?.target as HTMLElement)?.textContent?.trim() || '',
    });
  } catch (error) {
    if (!isDevelopment) {
      console.warn('Outbound link tracking failed:', error);
    }
  }
};

// Track CTA clicks
export const trackCTAClick = (ctaText: string, ctaLocation: string) => {
  try {
    trackEvent('cta_click', { 
      text: ctaText,
      location: ctaLocation,
      page: window.location.pathname,
    });
  } catch (error) {
    if (!isDevelopment) {
      console.warn('CTA click tracking failed:', error);
    }
  }
};

// Track navigation clicks
export const trackNavigationClick = (linkText: string, linkPath: string) => {
  try {
    trackEvent('navigation_click', {
      text: linkText,
      path: linkPath,
      previousPath: window.location.pathname,
    });
  } catch (error) {
    if (!isDevelopment) {
      console.warn('Navigation click tracking failed:', error);
    }
  }
};

// Track form interactions
export const trackFormInteraction = (formType: string, status: 'error' | 'success', details?: string) => {
  try {
    trackEvent(status === 'error' ? 'form_error' : 'form_success', {
      formType,
      details,
      page: window.location.pathname,
    });
  } catch (error) {
    if (!isDevelopment) {
      console.warn('Form interaction tracking failed:', error);
    }
  }
};

// Track tier selection
export const trackTierSelection = (tierName: string, tierLevel: number, spots: number) => {
  try {
    trackEvent('tier_selection', {
      name: tierName,
      level: tierLevel,
      remainingSpots: spots,
      page: window.location.pathname,
    });
  } catch (error) {
    if (!isDevelopment) {
      console.warn('Tier selection tracking failed:', error);
    }
  }
};

// Custom hook for tracking page views and scroll depth with error handling
export const usePageTracking = () => {
  const location = useLocation();

  useEffect(() => {
    try {
      trackPageView(location.pathname);

      // Track scroll depth
      let maxScroll = 0;
      const handleScroll = () => {
        try {
          const scrollPercent = Math.round(
            (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
          );
          
          if (scrollPercent > maxScroll && scrollPercent % 25 === 0) {
            maxScroll = scrollPercent;
            trackEvent('scroll_depth', { 
              depth: scrollPercent,
              page: location.pathname,
            });
          }
        } catch (error) {
          if (!isDevelopment) {
            console.warn('Scroll tracking failed:', error);
          }
        }
      };

      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    } catch (error) {
      if (!isDevelopment) {
        console.warn('Page tracking setup failed:', error);
      }
    }
  }, [location]);

  // Track outbound links
  useEffect(() => {
    try {
      const handleClick = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        const link = target.closest('a');
        
        if (link?.href && link.host !== window.location.host) {
          trackOutboundLink(link.href, link.textContent);
        }
      };

      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    } catch (error) {
      if (!isDevelopment) {
        console.warn('Outbound link tracking setup failed:', error);
      }
    }
  }, []);
};